import React, { useRef, useMemo, useEffect, useState, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  useGraphics,
  useDevicesList,
  useServiceMode,
  useDeviceConfigEntries,
  useSocketConnected,
  useSystemId,
  useGraphicsConfigs,
} from "../../hooks/useReduxSelectors";
import { fetchSVGGraphics, setSelectedView, setLastUsedCommand, removePendingCommand } from "../../ducks/graphicsDuck";
import GraphicalView2D from "../../components/graphicalView/2D/GraphicalView2D";
import { Box, CircularProgress } from "@mui/material";

import { Commands } from "../../actions/apiActions";
import { use } from "i18next";
import { Button, List, ListItemButton, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { kontram, status, surface } from "../../../theme/muiTheme";

// minor components
const HelperText = ({ text, textColor }) => {
  const isPending = text?.includes('Pending');
  return (
    <span style={{ color: textColor, fontSize: '20px' }}>
      {text}
      {isPending && <CircularProgress size={16} sx={{ ml: 1, color: 'white' }} />}
    </span>
  );
};

const LoadingIndicator = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
    <Box sx={{ color: 'white', fontSize: '35px', display: 'flex', justifyContent: 'center' }}>
      LOADING
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <CircularProgress sx={{ width: '100px !important', height: '100px !important', color: 'white' }} />
    </Box>
  </Box>
);

const NoGraphicsFound = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <Box sx={{ color: status.errorBright, fontSize: '35px', display: 'flex', justifyContent: 'center' }}>
    </Box>
  </Box>
);


// <GraphicalViewContainer>
const GraphicalUIContainer = ({ }) => {

  //**** Stuff from redux state 
  const systemId = useSystemId();
  const graphics = useGraphics();
  const objectWithSvgStrings = graphics?.graphicsStrings;
  const graphicsError = graphics?.error;
  const lastUserCommandFromUI = graphics?.lastUserCommandFromUI;
  const currentPendingCommands = graphics?.currentPendingCommands;
  const selectedView = graphics?.selectedView;
  const devices = useDevicesList();
  const serviceMode = useServiceMode();
  const serviceModeStatus = serviceMode?.status; // "AutoActive"/"ManualActive". GraphicalUi buttons are only functional during ManualActive
  const configEntries = useDeviceConfigEntries();
  const socketConnected = useSocketConnected();
  const graphicsViews = useGraphicsConfigs()?.views;

  const dispatch = useDispatch();
  // Are we using horizontal or vertical SVGString (depends on screen size)
  const [currentGraphicSVGString, setCurrentGraphicSVGString] = useState(objectWithSvgStrings?.horizontalGraphic);

  // This is used to send clarifying messages to the user
  const [helperText, setHelperText] = useState({
    text: "",
    textColor: "red"
  });

  const setHelperTextFunction = (newHelperTextWithColor) => {
    setHelperText(newHelperTextWithColor);
  };

  // useRef is used to create a reference to the graphicalView element in the DOM.
  const graphicalViewRef = useRef(null);

  // This function updates the SVG string based on the dimensions of the graphicalView element.
  const updateMedia = useCallback(() => {
    if (graphicalViewRef.current === null) { return; };
    if (graphicsError === true) { return; };
    if ((graphicalViewRef.current.offsetHeight > graphicalViewRef.current.offsetWidth) && (graphicalViewRef.current.offsetWidth < 1000)) {
      const modifiedSvgString = objectWithSvgStrings.verticalGraphic.replace(/width="(\d+)" height="(\d+)"/, `width="${graphicalViewRef.current.offsetWidth * 0.95}" height="${graphicalViewRef.current.offsetHeight * 0.95}"`);
      setCurrentGraphicSVGString(modifiedSvgString);
      // setCurrentGraphicSVGString(objectWithSvgStrings.verticalGraphic);
    } else {
      const modifiedSvgString = objectWithSvgStrings.horizontalGraphic.replace(/width="(\d+)" height="(\d+)"/, `width="${graphicalViewRef.current.offsetWidth * 0.95}" height="${graphicalViewRef.current.offsetHeight * 0.95}"`);
      setCurrentGraphicSVGString(modifiedSvgString);
      // setCurrentGraphicSVGString(objectWithSvgStrings.horizontalGraphic);

    }
  }, [objectWithSvgStrings, selectedView]);

  const fetchSVGGraphicsRequest = useCallback(() => {
    let args = {
      Name: selectedView?.name,
      Graphic: selectedView?.horizontal,
      Orientation: "horizontal"
    };
    dispatch(fetchSVGGraphics({ systemId, args }));
    args = {
      Name: selectedView?.name,
      Graphic: selectedView?.vertical,
      Orientation: "vertical"
    };
    dispatch(fetchSVGGraphics({ systemId, args }));
  }, [dispatch, systemId, selectedView]);

  useEffect(() => {
    // This function is called whenever the dimensions of the graphicalView element change.
    const handleResize = (entries) => {
      for (let entry of entries) {
        // Update the SVG string based on the new dimensions of the graphicalView element.
        updateMedia(entry.target);
      }
    };

    // Create a new ResizeObserver to observe changes in the dimensions of the graphicalView element.
    const resizeObserver = new ResizeObserver(handleResize);
    if (graphicalViewRef.current) {
      resizeObserver.observe(graphicalViewRef.current);
    }

    // Clean up the observer when the component is unmounted or the observed element changes.
    return () => {
      if (graphicalViewRef.current) {
        resizeObserver.unobserve(graphicalViewRef.current);
      }
    };
  }, [updateMedia]);

  // When socket is connected, send fetchrequest. If currentGraphicsSVGString already contain a SVGString, no need to fetch.
  useEffect(() => {
    fetchSVGGraphicsRequest();
  }, [socketConnected, selectedView]);

  // when graphics have been fetched to redux and objectWithSvgStrings is populated, set current graphic
  useEffect(() => {
    updateMedia();
  }, [objectWithSvgStrings, selectedView]);


  // When serviceModeStatus changes, reset the helperText and lastUsedCommands
  useEffect(() => {
    setHelperText({ text: "", textColor: "white" });
    if (serviceModeStatus === "AutoActive") {
      dispatch(setLastUsedCommand({ device: "", hwId: "", commandName: "", isActivating: "", systemId }));
    }
  }, [serviceModeStatus]);

  // Change SVG based on selectedView
  const updateSelectedSVG = useCallback((selectedViewItem) => {
    dispatch(setSelectedView({ selectedViewItem, systemId }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedView === null && graphicsViews?.length > 0) {
      updateSelectedSVG(graphicsViews[0]);
    }
  }, [graphicsViews]);

  // Data management and filtering

  // Map device's state to graph
  const deviceStateToGraph = useMemo(() => (devices || []).map((device) => {
    const deviceConfig = configEntries.find(entry => entry.hwId === device.hwId);
    const deviceData = {
      deviceTag: deviceConfig?.svgDeviceTag,
      hwId: deviceConfig.hwId,
      deviceBgColor: device.deviceBgColor,
      commandStates: (deviceConfig.commands || []).map((command) => {
        return {
          commandName: command.name,
          buttonTag: command.buttonTag,
          indicator: command.activeState?.indicator,
          colors: command.activeState?.colors,    // custom activeState colors, has default values
          blink: !command.activeState?.noblink,   // option to disable blinking
          independent: deviceConfig.independent ?? false,
          activeState: command.activeState && ((command.activeState !== undefined
            && (device.measurements.find(m => m.valueName === command.activeState?.valueName)?.value === command.activeState?.activeValue
              || device.diagnostics.find(m => m?.valueName === command.activeState?.valueName)?.value === command.activeState?.activeValue))
            ?? false),
          isPending: currentPendingCommands.some((pendingCommand) => pendingCommand.hwId === device.hwId && pendingCommand.commandName === command.name),
        };
      })
    };
    return deviceData;
  }), [devices, configEntries]);

  // Tag data to graph
  const taggedDataToGraph = useMemo(() => {
    let result = [];

    devices.forEach(device => {
      const deviceConfig = configEntries.find(entry => entry.hwId === device.hwId);
      const valueConfigs = deviceConfig.measurements.concat(deviceConfig.diagnostics);

      valueConfigs?.forEach(measurement => {
        if (measurement?.toGraph) {
          if (!result.some(t => t.tag === measurement.toGraph)) {
            result.push({ tag: measurement.toGraph, data: [] });
          }
          result.find(t => t.tag === measurement.toGraph).data.push({
            hwId: device.hwId,
            displayName: measurement.name,
            valueName: measurement.valueName,
            value: device.measurements.find(m => m.valueName === measurement.valueName)?.value ?? '-',
            isAlarmActive: device.measurements.find(m => m.valueName === measurement.valueName)?.isActive ?? false,
            category: device.measurements.find(m => m.valueName === measurement.valueName)?.category ?? '',
            unit: device.measurements.find(m => m.valueName === measurement.valueName)?.unit ?? ''
          });
        }
      });
    });
    return result;
  }, [devices, configEntries]);


  // console.log("deviceStateToGraph", deviceStateToGraph)
  // console.log("taggedDataToGraph", taggedDataToGraph)

  // Map button commands to devices
  const buttonMapping = useMemo(() => {
    // console.log("BUTTON MAPPING SET");
    return configEntries
      .map((device) => {
        if (device.commands?.some(cmd => cmd.hasOwnProperty('buttonTag'))) {
          return {
            hwId: device.hwId,
            commands: device.commands
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [configEntries]);


  // Store the last successful command as a ref
  const lastSuccessfulCommandRef = useRef(null);

  // When a command is successfully executed, show a helper text to the user
  useEffect(() => {
    deviceStateToGraph.forEach((device) => {
      device.commandStates.forEach((command) => {
        const pendingCommand = currentPendingCommands.find((cmd) => cmd.hwId === device.hwId && cmd.commandName === command.commandName);
        if (pendingCommand) {
          if ((command.activeState && pendingCommand.isActivating) || (!command.activeState && !pendingCommand.isActivating)) {
            setHelperTextFunction({
              text: `Command ${lastUserCommandFromUI.device}-${lastUserCommandFromUI.commandName} successful!`,
              textColor: kontram.primary,
            });
            console.log('removing pending command', pendingCommand);
            try {
              dispatch(removePendingCommand({ hwId: device.hwId, commandName: command.commandName, systemId }));
            } catch (e) { console.log(e); }
          }
          if (pendingCommand.timeCalled < Date.now() - 10000) {
            // setHelperTextFunction({
            //   text: `Command ${lastUserCommandFromUI.device}-${lastUserCommandFromUI.commandName} timeouted!`,
            //   textColor: "red",
            // });
            console.log('removing pending command', pendingCommand);
            try {
              dispatch(removePendingCommand({ hwId: device.hwId, commandName: command.commandName, systemId }));
            } catch (e) { console.log(e); }
          }
        }
      });
    });
  }, [deviceStateToGraph]);


  // menu for view selection
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const open = Boolean(anchorEl);
  const handleClickListItem = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  console.log("selectedView?.name", selectedView?.name);

  try {
    return (
      <Box ref={graphicalViewRef} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', flexDirection: 'column', height: '100%', width: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: 1 }}>
          {graphicsViews?.length > 1 &&
            <>
              <List
                component="nav"
                sx={{ padding: 0, margin: 0 }}
              >
                <ListItemButton
                  id="lock-button"
                  aria-haspopup="listbox"
                  aria-controls="view-select-menu"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClickListItem}
                  sx={{ border: `2px solid ${surface.divider}`, color: "white" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    <ListItemText
                      primary="Selected application"
                      secondary={selectedView?.name || "Select a view"}
                    />
                  </Stack>
                </ListItemButton>
              </List>
              <Menu
                id="view-select-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  'aria-labelledby': 'lock-button',
                  role: 'listbox',
                }}
              >
                {graphicsViews.map((view, index) => (
                  <MenuItem
                    key={view.name + "-index" + index}
                    // disabled={index === 0}
                    selected={index === selectedIndex}
                    onClick={(event) => {
                      handleMenuItemClick(event, index);
                      updateSelectedSVG(view);
                    }}
                  >
                    {view.name}
                  </MenuItem>
                ))}
              </Menu>
            </>
          }
          <HelperText text={helperText.text} textColor={helperText.textColor} />
        </Stack>
        <Box sx={{ height: "100%", overflow: "auto" }}>
          {currentGraphicSVGString ? (
            <GraphicalView2D
              svg={currentGraphicSVGString}
              systemId={systemId}
              serviceModeStatus={serviceModeStatus}
              deviceStateToGraph={deviceStateToGraph}
              taggedDataToGraph={taggedDataToGraph}
              setHelperTextFunction={setHelperTextFunction}
              lastUserCommandFromUI={lastUserCommandFromUI}
              buttonMapping={buttonMapping}
            />
          ) : graphicsError ? (
            <NoGraphicsFound />
          ) : (
            <LoadingIndicator />
          )}
        </Box>
      </Box>
    );
  } catch (e) {
    return (
      <Box ref={graphicalViewRef} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', flexDirection: 'column', flex: 1 }}>
        <HelperText text={e} textColor="red" />
      </Box>
    );
  }
};

export default withTranslation("common")(GraphicalUIContainer);
